import { Box, Paper, Typography, Button } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import InputField from "../../components/common/InputField";
import UploadCard from "../../components/lead/UploadCard";
import SelectField from "../../components/common/SelectField";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

import {
  getLoanById,
  createCommission,
  updateCommission,
} from "../../api/api";

import Section from "../../components/common/Section";
import { handleLeadRedirect } from "../../utils/leadRedirect";

// 🔥 FORMAT
const formatNumber = (num: number) =>
  new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num || 0);

const AddCommission = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();

  // 🔥 VIEW MODE
  const isView = location.pathname.includes("/view/");

  const [loan, setLoan] = useState<any>(null);
  const [loadingPage, setLoadingPage] = useState(true);

  const [form, setForm] = useState<any>({
    _id: null,

    bankPercentage: "",
    bankGst: "18",
    gstType: "exclude",

    brokerName: "",
    brokerMobile: "",
    brokerAmount: "",

    paymentMode: "upi",
    paymentProof: null,
  });

  const [existingFile, setExistingFile] = useState("");

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // ================= FETCH =================

  const fetchLoan = async () => {
    try {
      if (!id) return;

      const res = await getLoanById(id);

      const data = res.data?.data;

      const lead = data?.leadId;

      // 🔒 FLOW GUARD
      if (lead?.status !== "disbursed") {
        handleLeadRedirect(lead, navigate);
        return;
      }

      if (!data) {
        dispatch(
          showSnackbar({
            message: "Loan not found",
            type: "error",
          }),
        );

        navigate("/commission");

        return;
      }

      setLoan(data);

      // 🔥 EXISTING COMMISSION
      if (data?.commission) {
        const c = data.commission;

        setForm({
          _id: c._id,

          bankPercentage: c.bankPercentage || "",
          bankGst: c.bankGst || "18",
          gstType: c.gstType || "exclude",

          brokerName: c.brokerName || "",
          brokerMobile: c.brokerMobile || "",
          brokerAmount: c.brokerAmount || "",

          paymentMode: c.paymentMode || "upi",

          paymentProof: null,
        });

        if (c.paymentProof) {
          setExistingFile(c.paymentProof);
        }
      } else {
        // 🔥 CREATE MODE
        setForm((prev: any) => ({
          ...prev,

          brokerName:
            data?.leadId?.bankDetails?.brokerName || "",

          brokerMobile:
            data?.leadId?.bankDetails?.brokerMobile || "",
        }));
      }
    } catch (err) {
      console.log(err);

      dispatch(
        showSnackbar({
          message: "Failed to load data",
          type: "error",
        }),
      );

      navigate("/commission");
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    fetchLoan();
  }, [id]);

  // ================= HANDLE =================

  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prev: any) => ({
      ...prev,
      [key]: "",
    }));
  };

  // ================= CALCULATIONS =================

  const baseAmount = loan?.loanAmount || 0;

  // 🔥 COMMISSION
  const commissionAmount =
    baseAmount && form.bankPercentage
      ? (baseAmount * Number(form.bankPercentage)) / 100
      : 0;

  let gstAmount = 0;
  let finalAmount = 0;

  // 🔥 GST LOGIC
  if (form.gstType === "include") {
    // GST included

    finalAmount = commissionAmount;

    gstAmount =
      commissionAmount -
      commissionAmount /
        (1 + Number(form.bankGst || 0) / 100);
  } else {
    // GST extra

    gstAmount =
      form.bankGst && commissionAmount
        ? (commissionAmount *
            Number(form.bankGst)) /
          100
        : 0;

    finalAmount =
      commissionAmount + gstAmount;
  }

  // 🔥 EXPENSE
  const brokerAmount = Number(
    form.brokerAmount || 0,
  );

  // 🔥 NET PROFIT
  const profit =
    commissionAmount -
    gstAmount -
    brokerAmount;

  // ================= VALIDATION =================

  const validate = () => {
    const err: any = {};

    if (!form.bankPercentage) {
      err.bankPercentage =
        "Enter commission %";
    }

    if (Number(form.bankPercentage) > 50) {
      err.bankPercentage =
        "Max 50% allowed";
    }

    if (Number(form.bankGst) > 100) {
      err.bankGst =
        "Max 100% allowed";
    }

    if (!form.brokerAmount) {
      err.brokerAmount =
        "Enter broker amount";
    }

    return err;
  };

  // ================= SUBMIT =================

  const handleSubmit = async () => {
    if (!id) return;

    const err = validate();

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("loanId", id);

      formData.append(
        "bankPercentage",
        String(form.bankPercentage),
      );

      formData.append(
        "bankAmount",
        String(commissionAmount),
      );

      formData.append(
        "bankGst",
        String(form.bankGst || 0),
      );

      formData.append(
        "gstType",
        form.gstType,
      );

      formData.append(
        "brokerName",
        form.brokerName,
      );

      formData.append(
        "brokerMobile",
        form.brokerMobile,
      );

      formData.append(
        "brokerAmount",
        String(brokerAmount),
      );

      formData.append(
        "paymentMode",
        form.paymentMode,
      );

      if (form.paymentProof) {
        formData.append(
          "paymentProof",
          form.paymentProof,
        );
      }

      // 🔥 CREATE / UPDATE
      if (form._id) {
        await updateCommission(
          form._id,
          formData,
        );
      } else {
        await createCommission(formData);
      }

      dispatch(
        showSnackbar({
          message:
            "Commission saved successfully",
          type: "success",
        }),
      );

      navigate("/commission");
    } catch (err: any) {
      console.log(err);

      dispatch(
        showSnackbar({
          message:
            err?.message ||
            "Failed to save commission",

          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // ================= LOADING =================

  if (loadingPage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!loan) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>
          No Data Found
        </Typography>

        <Button
          onClick={() =>
            navigate("/commission")
          }
        >
          Go Back
        </Button>
      </Box>
    );
  }

  // ================= UI =================

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            mb: 3,
          }}
        >
          {isView
            ? "View Commission"
            : form._id
              ? "Update Commission"
              : "Add Commission"}
        </Typography>

        {/* LOAN DETAILS */}

        <Section title="Loan Details" />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <InputField
            label="Customer"
            value={
              loan?.leadId?.customerName
            }
            disabled
          />

          <InputField
            label="Lead ID"
            value={
              loan?.leadId?.leadCode
            }
            disabled
          />

          <InputField
            label="Loan Amount"
            value={formatNumber(baseAmount)}
            disabled
          />
        </Box>

        {/* BANK COMMISSION */}

        <Section title="Bank Commission" />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <InputField
            label="Bank % (Commission)"
            value={form.bankPercentage}
            disabled={isView}
            error={
              !!errors.bankPercentage
            }
            helperText={
              errors.bankPercentage
            }
            onChange={(e: any) =>
              handleChange(
                "bankPercentage",
                e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 2),
              )
            }
          />

          <SelectField
            name="gstType"
            label="GST Type"
            value={form.gstType}
            disabled={isView}
            onChange={(e: any) =>
              handleChange(
                "gstType",
                e.target.value,
              )
            }
            options={[
              {
                label:
                  "GST Excluded",
                value:
                  "exclude",
              },
              {
                label:
                  "GST Included",
                value:
                  "include",
              },
            ]}
          />

          <InputField
            label="GST %"
            value={form.bankGst}
            disabled={isView}
            error={!!errors.bankGst}
            helperText={
              errors.bankGst
            }
            onChange={(e: any) =>
              handleChange(
                "bankGst",
                e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 2),
              )
            }
          />
        </Box>

        {/* CALCULATIONS */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <InputField
            label="Commission Amount"
            value={formatNumber(
              commissionAmount,
            )}
            disabled
          />

          <InputField
            label="GST Amount"
            value={formatNumber(
              gstAmount,
            )}
            disabled
          />

          <InputField
            label="Final Received"
            value={formatNumber(
              finalAmount,
            )}
            disabled
          />
        </Box>

        {/* BROKER */}

        <Section title="Broker Details" />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <InputField
            label="Broker Name"
            value={form.brokerName}
            disabled={isView}
            onChange={(e: any) =>
              handleChange(
                "brokerName",
                e.target.value,
              )
            }
          />

          <InputField
            label="Broker Mobile"
            value={form.brokerMobile}
            disabled={isView}
            onChange={(e: any) =>
              handleChange(
                "brokerMobile",
                e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 10),
              )
            }
          />

          <InputField
            label="Broker Amount"
            value={form.brokerAmount}
            disabled={isView}
            error={
              !!errors.brokerAmount
            }
            helperText={
              errors.brokerAmount
            }
            onChange={(e: any) =>
              handleChange(
                "brokerAmount",
                e.target.value.replace(
                  /\D/g,
                  "",
                ),
              )
            }
          />
        </Box>

        {/* PAYMENT */}

        <Section title="Payment Details" />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr",
            },
            gap: 2,
            mb: 2,
            alignItems: "start",
          }}
        >
          <SelectField
            name="paymentMode"
            label="Payment Mode"
            value={form.paymentMode}
            disabled={isView}
            onChange={(e: any) =>
              handleChange(
                "paymentMode",
                e.target.value,
              )
            }
            options={[
              {
                label: "UPI",
                value: "upi",
              },
              {
                label: "Bank",
                value: "bank",
              },
              {
                label: "Cash",
                value: "cash",
              },
            ]}
          />

          <Box
            sx={{
              width: {
                xs: "100%",
                md: 250,
              },
            }}
          >
            <UploadCard
              name="paymentProof"
              label="Upload Payment Proof"
              file={
                form.paymentProof
              }
              existingFile={
                existingFile
              }
              disabled={isView}
              onChange={(
                name,
                file,
              ) =>
                handleChange(
                  name,
                  file,
                )
              }
            />
          </Box>
        </Box>

        {/* PROFIT */}

        <Section title="Profit Summary" />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md:
                "1fr 1fr 1fr 1fr",
            },
            gap: 2,
            mt: 2,
          }}
        >
          <InputField
            label="Income"
            value={formatNumber(
              commissionAmount,
            )}
            disabled
          />

          <InputField
            label="GST Liability"
            value={formatNumber(
              gstAmount,
            )}
            disabled
          />

          <InputField
            label="Broker Expense"
            value={formatNumber(
              brokerAmount,
            )}
            disabled
          />

          <InputField
            label="Net Profit"
            value={formatNumber(
              profit,
            )}
            disabled
          />
        </Box>

        {/* BUTTONS */}

        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() =>
              navigate("/commission")
            }
            sx={{
              borderRadius:
                "12px",
              textTransform:
                "none",
              px: 3,
            }}
          >
            Cancel
          </Button>

          {!isView && (
            <Button
              variant="contained"
              onClick={
                handleSubmit
              }
              disabled={loading}
              sx={{
                borderRadius:
                  "12px",
                textTransform:
                  "none",
                px: 3,
                background:
                  "linear-gradient(135deg, var(--primary), var(--primary-light))",
              }}
            >
              {loading
                ? "Processing..."
                : form._id
                  ? "Update Commission"
                  : "Save Commission"}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AddCommission;